import { AxiosResponse } from 'axios';
import { storageService } from '@/shared/services/storageService';
import { configs } from '@/shared/configs';
import { httpClient, DirectionService, EDirection } from '@/shared/services';
import { i18n } from '@/i18n';

const LANG_KEY = 'lang';
const STATUS = 'status';

export type AllowedLocales = 'en' | 'he';

export const localeService = {
  setDirection(lang: AllowedLocales) {
    const direction = lang === 'he' ? EDirection.RTL : EDirection.LTR;

    DirectionService.set(direction);
  },

  setLang(lang: AllowedLocales) {
    // todo: check store before
    storageService.session.set(LANG_KEY, lang);
    this.setDirection(lang);
  },

  getLang(): string {
    // todo: check store before
    return storageService.session.get(LANG_KEY) ?? configs.DEFAULT_LANG;
  },

  getGlobalTranslations(locale: AllowedLocales = 'en', basePath = '/ui-assets/access/configurations') {
    return httpClient.client
      .get(`${basePath}/global/translations/global.${locale}.json`)
      // @ts-ignore
      .then((response: AxiosResponse) => {
        const translations = response.data;

        localeService.addTranslation(locale, translations);
      });
  },

  /**
   * Process gender entries, returns rest as is
   *
   * {
   *   _gender: {
   *     male: 'Translation-for-male',
   *     female: 'Translation-for-female',
   *   }
   * }
   *
   * @param translationEntry
   */
  processGenderObject(translationEntry: any): any {
    const genderSpecificData = translationEntry?._gender || translationEntry?.$gender;

    if (!genderSpecificData) {
      return translationEntry;
    }

    return JSON.stringify(translationEntry);
  },

  /**
   * Process special translation entries (gender-specific messages, etc)
   *
   * @param translations
   */
  processEntries(translations: any) {
    const processedTranslation: any = {};

    Object.entries(translations).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        value = localeService.processEntries(value);
      }

      processedTranslation[key] = localeService.processGenderObject(value);
    });

    return processedTranslation;
  },

  /**
   * Traverse translations object, and preprocess the data
   *
   * @param locale
   * @param translations
   */
  addTranslation(locale: AllowedLocales = 'en', translations: any = {}) {
    const processedTranslations = localeService.processEntries(translations);

    i18n.global.locale.value = locale;
    i18n.global.setLocaleMessage(locale, {
      ...(i18n.global.messages as any)[locale],
      ...processedTranslations,
    });
  },

  setStatus(status: string) {
    storageService.session.set(STATUS, status);
  },

  getStatus(): string {
    return storageService.session.get(STATUS);
  },

  getValidationTranslations(locale: AllowedLocales = 'en'): any {
    return i18n.global.getLocaleMessage(locale)?.validations;
  },
};
